// src/utils/exportPOToWord.js
// ====================================================================
// PO (Đơn đặt hàng) - MAPPING GIỐNG SO, có recalc totals theo items đã chọn
// - Merge với public/phieuPO.docx
// ====================================================================

import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';

import { getApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, collection, query, where, limit, getDocs,
} from 'firebase/firestore';

/* -------------------- Helpers -------------------- */
const toDate = (v) => {
  if (!v) return null;
  try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return null; }
};
const pad2 = (n) => String(n).padStart(2, '0');
const num  = (v) => Number(v ?? 0);
const VND  = (n) => new Intl.NumberFormat('vi-VN', { style:'currency', currency:'VND' }).format(num(n));
const UPPER = (s) => String(s ?? '').toLocaleUpperCase('vi-VN');
const pick = (obj, paths, fallback = '') => {
  for (const p of paths) {
    const val = p.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
    if (val !== undefined && val !== null && val !== '') return val;
  }
  return fallback;
};
const publicURL = (name) => `${window.location.origin}/${name}`;

/* ---- Number → chữ Việt ---- */
const VN_DIGITS = ['không','một','hai','ba','bốn','năm','sáu','bảy','tám','chín'];
const VN_UNITS  = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ', 'tỷ tỷ'];

function readTriplet(n, { showZeroHundred = false } = {}) {
  const h = Math.floor(n / 100), t = Math.floor((n % 100) / 10), u = n % 10;
  const parts = [];
  if (h > 0) parts.push(VN_DIGITS[h], 'trăm');
  else if (showZeroHundred) parts.push('không', 'trăm');

  if (t > 1) {
    parts.push(VN_DIGITS[t], 'mươi');
    if (u === 1) parts.push('mốt');
    else if (u === 4) parts.push('tư');
    else if (u === 5) parts.push('lăm');
    else if (u > 0) parts.push(VN_DIGITS[u]);
  } else if (t === 1) {
    parts.push('mười');
    if (u === 5) parts.push('lăm');
    else if (u > 0) parts.push(VN_DIGITS[u]);
  } else if (u > 0) {
    if (h > 0 || showZeroHundred) parts.push('lẻ');
    parts.push(VN_DIGITS[u]);
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}
function numberToWordsVI(n) {
  let x = Math.round(Number(n || 0));
  if (x === 0) return 'không';
  let sign = '';
  if (x < 0) { sign = 'âm '; x = Math.abs(x); }
  const groups = [];
  while (x > 0) { groups.push(x % 1000); x = Math.floor(x / 1000); }
  const top = groups.length - 1;
  const out = [];
  for (let i = top; i >= 0; i--) {
    const val = groups[i];
    if (val === 0) continue;
    const showZeroHundred = (i < top) && (val < 100) && (val % 100 > 0);
    const chunk = readTriplet(val, { showZeroHundred });
    if (chunk) {
      out.push(chunk);
      if (VN_UNITS[i]) out.push(VN_UNITS[i]);
    }
  }
  return out.join(' ').replace(/\s+/g, ' ').trim();
}
function moneyToWordsVI(n, { capitalize = true, addDong = true } = {}) {
  let w = numberToWordsVI(n);
  if (addDong) w += ' đồng' + (Number(n) % 1 === 0 ? ' chẵn' : '');
  if (capitalize && w) w = w.charAt(0).toUpperCase() + w.slice(1);
  return w;
}

/* ---- Lỗi Docxtemplater chi tiết ---- */
function formatDocxError(err) {
  const props = err?.properties;
  if (Array.isArray(props?.errors) && props.errors.length) {
    return props.errors.map((e, i) => {
      const tag =
        e.properties?.name ||
        e.properties?.id ||
        e.properties?.xmlTag ||
        (e.properties?.explanation || '').match(/\{[^}]+\}/)?.[0] ||
        'unknown';
      const where = [
        e.properties?.file,
        e.properties?.position?.start != null ? `offset ${e.properties.position.start}` : '',
      ].filter(Boolean).join(' ');
      const why = e.properties?.explanation || e.message || 'Unknown';
      return `#${i + 1} ${tag}: ${why}${where ? ` (${where})` : ''}`;
    }).join('\n');
  }
  return err?.message || String(err);
}

/* -------------------- Firestore: customers (giống SO) -------------------- */
async function fetchOneById(db, col, id) {
  const snap = await getDoc(doc(db, col, String(id)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
async function fetchOneByField(db, col, field, value) {
  const qs = await getDocs(query(collection(db, col), where(field, '==', value), limit(1)));
  if (!qs.empty) { const s = qs.docs[0]; return { id: s.id, ...s.data() }; }
  return null;
}
async function fetchCustomer(order) {
  try {
    const app = getApp(); const db = getFirestore(app);
    const byId = order?.customerId || (order?.customerRef && (order.customerRef.id || order.customerRef));
    if (byId) { const r = await fetchOneById(db, 'customers', byId); if (r) return r; }
    if (order?.customerCode) { const r = await fetchOneByField(db, 'customers', 'code', order.customerCode); if (r) return r; }
    if (order?.customerName) { const r = await getDocs(query(collection(db, 'customers'), where('name', '==', order.customerName), limit(1)));
      if (!r.empty) { const s = r.docs[0]; return { id: s.id, ...s.data() }; } }
  } catch (e) { console.warn('fetchCustomer error:', e); }
  return null;
}
async function attachCustomer(order) {
  if (!order) return order;
  if (order.customer && (order.customer.name || order.customer.address || order.customer.code)) return order;
  const c = await fetchCustomer(order);
  return c ? { ...order, customer: c } : order;
}

/* -------------------- Recalc totals from selected items -------------------- */
function recalcFinancials(order = {}) {
  const n = (v) => Number(v ?? 0);
  const items = Array.isArray(order.items) ? order.items : [];

  let subtotal = 0;
  const mapped = items.map((it) => {
    const q = n(it.quantity);
    const p = n(it.exportPrice ?? it.price ?? it.unitPrice ?? it.importPrice);
    const s = q * p;
    subtotal += s;
    return { ...it, __q: q, __p: p, __s: s };
  });

  const shippingFee     = n(order.shippingFee);
  const amountPaid      = n(order.amountPaid);
  const taxRate         = n(order.taxRate);
  let   discountRate    = n(order.discountRate);
  let   discountAmount  = n(order.discountAmount);

  if (discountRate > 0) {
    discountAmount = Math.round(subtotal * discountRate / 100);
  } else if (discountAmount > 0) {
    discountRate = subtotal ? (discountAmount / subtotal) * 100 : 0;
  }

  const taxableBase = Math.max(0, subtotal - discountAmount);
  const taxAmount   = Math.round(taxableBase * taxRate / 100);
  const grandTotal  = taxableBase + taxAmount + shippingFee;
  const remainingAmount = grandTotal - amountPaid;

  return {
    items: mapped, subtotal, discountRate, discountAmount,
    taxRate, taxAmount, shippingFee, grandTotal, amountPaid, remainingAmount,
  };
}

/* -------------------- BUILD DATA: SO-style cho PO -------------------- */
function buildSOStyleData(order = {}, company = {}) {
  const soNumber = order.soNumber || order.poNumber || '';
  const soDate   = toDate(order.soDate || order.poDate || order.createdAt);

  const fin = recalcFinancials(order);

  const items = (fin.items || []).map((it, i) => ({
    idx: i + 1,
    productName: it.productName || it.name || '',
    quantity: it.__q,
    exportPrice: it.__p,
    exportPriceVND: VND(it.__p),
    lineTotal: it.__s,
    lineTotalVND: VND(it.__s),
  }));

  const rep  = pick(company, ['representative','legalRepresentative'], '');
  const bank = pick(company, ['bankAccount.number','bankAccount','accountNumber','bankAccountNo'], '');

  return {
    // SO-style fields
    so_number: soNumber,
    so_day:    soDate ? pad2(soDate.getDate())       : '',
    so_month:  soDate ? pad2(soDate.getMonth() + 1)  : '',
    so_year:   soDate ? String(soDate.getFullYear()) : '',

    company_name: company.name || '',
    company_address: company.address || '',
    company_phone: company.phone || '',
    company_tax: company.taxCode || '',
    company_representative: rep,
    company_representative_upper: UPPER(rep),
    company_bank_account: bank,
    company: {
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      taxCode: company.taxCode || '',
      representative: rep,
      representativeUpper: UPPER(rep),
      bankAccount: bank,
    },

    customer_name:          pick(order, ['customerName','customer.name'], ''),
    customer_address:       pick(order, ['customerAddress','customer.address','address'], ''),
    customer_code:          pick(order, ['customerCode','customer.code','code'], ''),
    customer_representative:pick(order, ['customerRepresentative','customer.representative'], ''),
    customer_position:      pick(order, ['customerPosition','customer.position'], ''),
    customer: {
      name:          pick(order, ['customer.name','customerName'], ''),
      address:       pick(order, ['customer.address','customerAddress','address'], ''),
      code:          pick(order, ['customer.code','customerCode','code'], ''),
      representative:pick(order, ['customer.representative','customerRepresentative'], ''),
      position:      pick(order, ['customer.position','customerPosition'], ''),
      phone:         pick(order, ['customer.phone'], ''),
      email:         pick(order, ['customer.email'], ''),
      note:          pick(order, ['customer.note'], ''),
    },

    items,

    // Totals (ĐÃ TÍNH LẠI)
    subtotal: fin.subtotal,             subtotalVND: VND(fin.subtotal),
    discountRate: fin.discountRate,
    discountAmount: fin.discountAmount, discountAmountVND: VND(fin.discountAmount),
    taxRate: fin.taxRate,               taxAmount: fin.taxAmount,      taxAmountVND: VND(fin.taxAmount),
    shippingFee: fin.shippingFee,       shippingFeeVND: VND(fin.shippingFee),
    grandTotal: fin.grandTotal,         grandTotalVND: VND(fin.grandTotal),
    amountPaid: fin.amountPaid,         amountPaidVND: VND(fin.amountPaid),
    remainingAmount: fin.remainingAmount, remainingAmountVND: VND(fin.remainingAmount),

    grandTotalText:      moneyToWordsVI(fin.grandTotal),
    remainingAmountText: moneyToWordsVI(fin.remainingAmount),
  };
}

/* -------------------- Merge DOCX -------------------- */
async function mergePOWithTemplate(data, templateName = 'phieuPO.docx') {
  const res = await fetch(publicURL(templateName));
  if (!res.ok) throw new Error(`Không tìm thấy /${templateName} trên Hosting`);
  const ab = await res.arrayBuffer();

  const zip = new PizZip(ab);
  const ok = Object.keys(zip.files || {}).some((k) => /^word\/document\.xml$/i.test(k));
  if (!ok) {
    const entries = Object.keys(zip.files || {});
    throw new Error(`Template không hợp lệ (thiếu word/document.xml). ZIP: ${entries.join(', ')}`);
  }

  const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true, nullGetter: () => '' });

  if (typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production') {
    console.log('[PO (SO-style)] keys:', Object.keys(data));
    console.log('[PO (SO-style)] first item:', (data.items || [])[0]);
  }

  doc.setData(data);

  try {
    doc.render();
  } catch (err) {
    console.error('PO docx render error:', err, err?.properties?.errors);
    throw new Error(`Placeholder sai/thiếu trong 'phieuPO.docx':\n${formatDocxError(err)}`);
  }

  return doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

/* ===================== Public API ===================== */
export async function exportPODocxLikeSO(order, company = {}) {
  try {
    const enriched = await attachCustomer(order);
    const data = buildSOStyleData(enriched, company);
    const blob = await mergePOWithTemplate(data, 'phieuPO.docx');
    saveAs(blob, `PO_${data.so_number || 'don-dat-hang'}.docx`);
  } catch (e) {
    console.error('Xuất PO DOCX (SO-style) lỗi:', e);
    alert(String(e?.message || e));
  }
}

/** (Tuỳ chọn) Xuất PO → PDF nếu có API chuyển đổi */
export async function exportPOPdfLikeSO(order, company = {}) {
  try {
    const enriched = await attachCustomer(order);
    const payload = { order: enriched, company, templateUrl: publicURL('phieuPO.docx') };
    const res = await fetch('/api/contract/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `API lỗi ${res.status}`);
    }
    const buf = await res.arrayBuffer();
    const blob = new Blob([buf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `PO_${enriched?.soNumber || enriched?.poNumber || 'don-dat-hang'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Xuất PO PDF (SO-style) lỗi:', e);
    alert(String(e?.message || e));
  }
}
